import {
  Box,
  Container,
  Grid,
  Typography,
  Card,
  CardContent,
  useTheme,
  useMediaQuery,
} from "@mui/material";
import { motion } from "framer-motion";
import VerifiedUserIcon from "@mui/icons-material/VerifiedUser";
import RecyclingIcon from "@mui/icons-material/Recycling";
import AccessTimeIcon from "@mui/icons-material/AccessTime";
import ThumbUpIcon from "@mui/icons-material/ThumbUp";

const features = [
  {
    icon: <VerifiedUserIcon />,
    title: "Trusted & Vetted Cleaners",
    description:
      "All our cleaning professionals are thoroughly background-checked, trained, and insured for your peace of mind.",
    gradient: "linear-gradient(135deg, #667eea 0%, #764ba2 100%)",
    animation: "pulse",
  },
  {
    icon: <RecyclingIcon />,
    title: "Eco-Friendly Products",
    description:
      "We use only environmentally safe, non-toxic cleaning products that are safe for your family and pets.",
    gradient: "linear-gradient(135deg, #11998e 0%, #38ef7d 100%)",
    animation: "spin",
  },
  {
    icon: <AccessTimeIcon />,
    title: "On-Time Service",
    description:
      "We value your time. Our team arrives punctually and completes the job efficiently without compromising quality.",
    gradient: "linear-gradient(135deg, #fc466b 0%, #3f5efb 100%)",
    animation: "bounce",
  },
  {
    icon: <ThumbUpIcon />,
    title: "Guaranteed Satisfaction",
    description:
      "100% satisfaction guarantee. If you're not completely happy, we'll make it right or provide a full refund.",
    gradient: "linear-gradient(135deg, #f093fb 0%, #f5576c 100%)",
    animation: "tada",
  },
];

const FeaturesGrid = () => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("md"));

  const iconAnimations = {
    pulse: {
      scale: [1, 1.1, 1],
      transition: {
        duration: 2,
        repeat: Infinity,
        ease: "easeInOut",
      },
    },
    spin: {
      rotate: [0, 360],
      transition: {
        duration: 8,
        repeat: Infinity,
        ease: "linear",
      },
    },
    bounce: {
      y: [0, -10, 0],
      transition: {
        duration: 2,
        repeat: Infinity,
        ease: "easeInOut",
      },
    },
    tada: {
      scale: [1, 1.1, 1],
      rotate: [0, -5, 5, -5, 0],
      transition: {
        duration: 2,
        repeat: Infinity,
        repeatDelay: 3,
      },
    },
  };

  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.2,
      },
    },
  };

  const itemVariants = {
    hidden: { opacity: 0, y: 50 },
    visible: {
      opacity: 1,
      y: 0,
      transition: {
        duration: 0.6,
        ease: "easeOut",
      },
    },
  };

  return (
    <Box
      sx={{
        py: { xs: 6, md: 10 },
        backgroundColor: "background.default",
        position: "relative",
        overflow: "hidden",
      }}
    >
      {/* Animated Background Elements */}
      <motion.div
        style={{
          position: "absolute",
          top: "10%",
          left: "5%",
          width: 120,
          height: 120,
          borderRadius: "50%",
          background: "linear-gradient(45deg, #e3f2fd, #bbdefb)",
          opacity: 0.3,
        }}
        animate={{
          y: [0, -20, 0],
          scale: [1, 1.1, 1],
        }}
        transition={{
          duration: 6,
          repeat: Infinity,
          ease: "easeInOut",
        }}
      />
      <motion.div
        style={{
          position: "absolute",
          bottom: "15%",
          right: "7%",
          width: 80,
          height: 80,
          borderRadius: "30%",
          background: "linear-gradient(45deg, #f3e5f5, #e1bee7)",
          opacity: 0.2,
          rotate: 45,
        }}
        animate={{
          y: [0, 15, 0],
          rotate: [45, 90, 45],
        }}
        transition={{
          duration: 8,
          repeat: Infinity,
          ease: "easeInOut",
          delay: 1,
        }}
      />

      <Container>
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-50px" }}
          transition={{ duration: 0.8 }}
        >
          <Typography
            variant={isMobile ? "h3" : "h2"}
            align="center"
            sx={{
              mb: 2,
              fontWeight: 800,
              background: "linear-gradient(45deg, #1976d2, #42a5f5)",
              backgroundClip: "text",
              WebkitBackgroundClip: "text",
              color: "transparent",
            }}
          >
            Why Choose Blossom Cleaning?
          </Typography>
          <Typography
            variant={isMobile ? "body1" : "h6"}
            align="center"
            color="text.secondary"
            sx={{
              mb: 8,
              maxWidth: 700,
              mx: "auto",
              fontSize: { xs: "1rem", md: "1.25rem" },
              lineHeight: 1.6,
            }}
          >
            We go above and beyond to deliver exceptional cleaning services that
            exceed your expectations
          </Typography>
        </motion.div>

        <motion.div
          variants={containerVariants}
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true, margin: "-50px" }}
        >
          <Grid
            container
            spacing={4}
            sx={{
              display: "flex",

              alignItems: "center",
              justifyContent: "center",
            }}
          >
            {features.map((feature, index) => (
              <Grid
                item
                xs={12}
                sm={6}
                md={3}
                key={index}
                sx={{ display: "flex" }}
              >
                <motion.div
                  variants={itemVariants}
                  whileHover={{
                    y: -15,
                    transition: { duration: 0.3 },
                  }}
                  style={{ width: "100%" }}
                >
                  <Card
                    sx={{
                      height: "100%",
                      textAlign: "center",
                      maxWidth: 400,
                      p: 3,
                      borderRadius: 4,
                      background: `linear-gradient(145deg, ${theme.palette.background.paper} 0%, ${theme.palette.background.default} 100%)`,
                      border: `1px solid ${theme.palette.divider}`,
                      position: "relative",
                      overflow: "hidden",
                      transition:
                        "all 0.4s cubic-bezier(0.25, 0.46, 0.45, 0.94)",
                      "&::before": {
                        content: '""',
                        position: "absolute",
                        top: 0,
                        left: 0,
                        right: 0,
                        height: 4,
                        background: feature.gradient,
                      },
                      "&:hover": {
                        transform: "translateY(-15px)",
                        boxShadow: `
                          0 25px 50px -12px rgba(0, 0, 0, 0.25),
                          0 0 30px -10px ${theme.palette.primary.main}40
                        `,
                        "& .feature-icon-container": {
                          background: feature.gradient,
                          "& svg": {
                            color: "white !important",
                          },
                        },
                      },
                    }}
                  >
                    <CardContent sx={{ p: 0, "&:last-child": { pb: 0 } }}>
                      {/* Animated Icon */}
                      <motion.div
                        className="feature-icon-container"
                        animate={iconAnimations[feature.animation]}
                        style={{
                          width: 80,
                          height: 80,
                          borderRadius: "50%",
                          background: theme.palette.background.default,
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "center",
                          margin: "0 auto 24px auto",
                          border: `2px solid ${theme.palette.divider}`,
                          position: "relative",
                          overflow: "hidden",
                        }}
                      >
                        <Box
                          sx={{
                            color: theme.palette.primary.main,
                            fontSize: 48,
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center",
                          }}
                        >
                          {feature.icon}
                        </Box>

                        {/* Pulsing Ring Effect */}
                        <motion.div
                          style={{
                            position: "absolute",
                            top: 0,
                            left: 0,
                            right: 0,
                            bottom: 0,
                            border: `2px solid ${theme.palette.primary.main}`,
                            borderRadius: "50%",
                            opacity: 0,
                          }}
                          animate={{
                            scale: [1, 1.5, 2],
                            opacity: [0.5, 0.2, 0],
                          }}
                          transition={{
                            duration: 3,
                            repeat: Infinity,
                            ease: "easeOut",
                          }}
                        />
                      </motion.div>

                      <Typography
                        variant="h6"
                        gutterBottom
                        sx={{
                          fontWeight: 700,
                          mb: 2,
                          background: feature.gradient,
                          backgroundClip: "text",
                          WebkitBackgroundClip: "text",
                          color: "transparent",
                        }}
                      >
                        {feature.title}
                      </Typography>

                      <Typography
                        variant="body2"
                        color="text.secondary"
                        sx={{
                          lineHeight: 1.7,
                          fontSize: "0.95rem",
                        }}
                      >
                        {feature.description}
                      </Typography>

                      {/* Hover Effect Border */}
                      <Box
                        sx={{
                          position: "absolute",
                          top: 0,
                          left: 0,
                          right: 0,
                          bottom: 0,
                          border: `2px solid transparent`,
                          borderRadius: 4,
                          background: `linear-gradient(145deg, ${theme.palette.primary.main}20, ${theme.palette.secondary.main}20) border-box`,
                          mask: `linear-gradient(#fff 0 0) padding-box, linear-gradient(#fff 0 0)`,
                          maskComposite: "exclude",
                          opacity: 0,
                          transition: "opacity 0.3s ease",
                          pointerEvents: "none",
                          "&:hover": {
                            opacity: 1,
                          },
                        }}
                      />
                    </CardContent>
                  </Card>
                </motion.div>
              </Grid>
            ))}
          </Grid>
        </motion.div>
      </Container>
    </Box>
  );
};

export default FeaturesGrid;
